import { Formik, useField } from 'formik';
import { useState } from 'react';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
  toEnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  AI_INTERVIEW_INTEGRITY_MONITORING_LABEL,
  ASSESSMENT_INTEGRITY_MONITORING_LABEL,
  INTEGRITY_MONITORING_AVAILABLE_LABEL,
  INTEGRITY_MONITORING_DATA_TEST_ID,
  SWITCH_OFF_NOTIFICATION_MESSAGE,
  getStageDefaultNotificationMessage,
} from './IntegrityMonitoringSection.constants';
import styles from './IntegrityMonitoringSection.module.scss';

type IntegrityMonitoringSectionFormValues = {
  integrityMonitoringAvailable: string;
  assessmentIntegrityMonitoring: string;
  aiInterviewIntegrityMonitoring: string;
};

type NotificationVisibility = {
  available?: boolean;
  assessment?: boolean;
  aiInterview?: boolean;
};

type IntegrityMonitoringSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const IntegrityMonitoringSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: IntegrityMonitoringSectionFormProps) => {
  const { handleMsgType } = useNotification();
  const [notificationVisibility, setNotificationVisibility] =
    useState<NotificationVisibility>({});

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: IntegrityMonitoringSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isIntegrityMonitoringAvailable:
          values.integrityMonitoringAvailable === EnableDisableValue.Enabled,
        isIntegrityMonitoringAssessmentDefaultEnabled:
          values.assessmentIntegrityMonitoring === EnableDisableValue.Enabled,
        isIntegrityMonitoringAiInterviewDefaultEnabled:
          values.aiInterviewIntegrityMonitoring === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Integrity monitoring settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Integrity monitoring settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    integrityMonitoringAvailable: toEnableDisableValue(
      company.isIntegrityMonitoringAvailable
    ),
    assessmentIntegrityMonitoring: toEnableDisableValue(
      company.isIntegrityMonitoringAssessmentDefaultEnabled
    ),
    aiInterviewIntegrityMonitoring: toEnableDisableValue(
      company.isIntegrityMonitoringAiInterviewDefaultEnabled
    ),
  };

  return (
    <Formik<IntegrityMonitoringSectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm, values, setFieldValue }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Integrity monitoring"
          formId="integrity-monitoring-form"
          hasResetAllButton={false}
          data-testid={INTEGRITY_MONITORING_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="integrity-monitoring-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="integrityMonitoringAvailable"
                name="integrityMonitoringAvailable"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label={INTEGRITY_MONITORING_AVAILABLE_LABEL}
                data-testid={`${INTEGRITY_MONITORING_DATA_TEST_ID}__available-radio-group`}
                useFormikField={useField}
                onChange={() => {
                  setFieldValue(
                    'assessmentIntegrityMonitoring',
                    EnableDisableValue.Disabled
                  );
                  setFieldValue(
                    'aiInterviewIntegrityMonitoring',
                    EnableDisableValue.Disabled
                  );
                  setNotificationVisibility({ available: true });
                }}
                className={styles.radioGroup}
              />
              {notificationVisibility.available &&
                values.integrityMonitoringAvailable ===
                  EnableDisableValue.Disabled && (
                  <InlineNotification
                    notificationType={NotificationType.NEUTRAL}
                    className={styles.inlineNotification}
                  >
                    {SWITCH_OFF_NOTIFICATION_MESSAGE}
                  </InlineNotification>
                )}
            </div>
            <div className={styles.nestedFields}>
              <div>
                <FormikRadioButtonGroup
                  id="assessmentIntegrityMonitoring"
                  name="assessmentIntegrityMonitoring"
                  options={enableDisableRadioButtonOptions}
                  layout="horizontal"
                  label={ASSESSMENT_INTEGRITY_MONITORING_LABEL}
                  data-testid={`${INTEGRITY_MONITORING_DATA_TEST_ID}__assessment-radio-group`}
                  useFormikField={useField}
                  disabled={
                    values.integrityMonitoringAvailable !==
                    EnableDisableValue.Enabled
                  }
                  onChange={() =>
                    setNotificationVisibility(prev => ({
                      ...prev,
                      assessment: true,
                    }))
                  }
                  className={styles.radioGroup}
                />
                {notificationVisibility.assessment && (
                  <InlineNotification
                    notificationType={NotificationType.NEUTRAL}
                    className={styles.inlineNotification}
                  >
                    {getStageDefaultNotificationMessage(
                      'assessment',
                      values.assessmentIntegrityMonitoring ===
                        EnableDisableValue.Enabled
                    )}
                  </InlineNotification>
                )}
              </div>
              <div>
                <FormikRadioButtonGroup
                  id="aiInterviewIntegrityMonitoring"
                  name="aiInterviewIntegrityMonitoring"
                  options={enableDisableRadioButtonOptions}
                  layout="horizontal"
                  label={AI_INTERVIEW_INTEGRITY_MONITORING_LABEL}
                  data-testid={`${INTEGRITY_MONITORING_DATA_TEST_ID}__ai-interview-radio-group`}
                  useFormikField={useField}
                  disabled={
                    values.integrityMonitoringAvailable !==
                    EnableDisableValue.Enabled
                  }
                  onChange={() =>
                    setNotificationVisibility(prev => ({
                      ...prev,
                      aiInterview: true,
                    }))
                  }
                  className={styles.radioGroup}
                />
                {notificationVisibility.aiInterview && (
                  <InlineNotification
                    notificationType={NotificationType.NEUTRAL}
                    className={styles.inlineNotification}
                  >
                    {getStageDefaultNotificationMessage(
                      'AI interview',
                      values.aiInterviewIntegrityMonitoring ===
                        EnableDisableValue.Enabled
                    )}
                  </InlineNotification>
                )}
              </div>
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default IntegrityMonitoringSectionForm;
